// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Outlook-style swipe on a phone mail row. The row lifts into a
// rounded card and follows the finger across the whole width. A tinted track
// with the action's icon sits behind it; past the commit line the colour
// floods, the icon pops and the phone ticks. Letting go there runs the full
// action: archive/delete slide the row off and collapse it, the rest spring
// back. Short of the line, a side with a tray rests open on it (one row at a
// time; a tap anywhere else closes it). The drag writes straight to the DOM
// once per frame, so the list never re-renders while a finger is down.
// Once the drag is sideways the row captures the pointer and blocks the
// scroller, so the release always lands here even when the finger drifts
// onto the next row; armed at release (or if the system steals the touch)
// always runs the action, it never leaves the row hanging open.
// Home adds an optional second action per side (leadingLong / trailingLong)
// that arms at a further line with its own colour and a second tick, and a
// 'card' look (rounded, inset track that stays grey until armed, labels);
// without those props the row behaves exactly as it does in mail.

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {swipeBackOwnsTouch} from '@app/features/app/utils/SwipeBack';
import {
	commitDistance,
	DEFAULT_COMMIT_RATIO,
	DEFAULT_LONG_RATIO,
	dragOffset,
	iconCentre,
	isArmed,
	releaseOutcome,
	resolveSwipeAxis,
	type SwipeReach,
	type SwipeReaches,
	type SwipeSides,
	type SwipeTrays,
	swipeProgress,
	swipeReach,
	trayWidth,
} from '@app/features/konvo_mail/components/MailSwipeModel';
import styles from '@app/features/konvo_mail/components/MailSwipeRow.module.css';
import {hapticTap} from '@app/features/platform/utils/Haptics';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useRef} from 'react';

export type MailSwipeTone = 'read' | 'archive' | 'delete' | 'flag' | 'reply' | 'remind' | 'task' | 'move' | 'pin';

export interface MailSwipeSpec {
	key: string;
	label: string;
	icon: React.ReactNode;
	tone: MailSwipeTone;
	/** A colour of its own instead of the tone's (Home's actions carry their own palette). */
	color?: string;
	/** The row leaves the list, so it slides off and collapses before `run`. */
	removes: boolean;
	run: () => void;
}

interface MailSwipeRowProps {
	/** Runs on a full swipe to the right; its track is uncovered on the left. */
	leading: MailSwipeSpec | null;
	/** Runs on a full swipe to the left; its track is uncovered on the right. */
	trailing: MailSwipeSpec | null;
	/** Runs on a longer swipe to the right, past the second line (a second haptic tick). */
	leadingLong?: MailSwipeSpec | null;
	/** Runs on a longer swipe to the left, past the second line. */
	trailingLong?: MailSwipeSpec | null;
	/** Share of the width the second line sits at. */
	longRatio?: number;
	/** 'card': a rounded, inset track that stays grey until armed, with the action's name beside its icon. */
	variant?: 'mail' | 'card';
	/** Rests open on the left after a short swipe to the right. */
	leadingTray?: ReadonlyArray<MailSwipeSpec>;
	/** Rests open on the right after a short swipe to the left. */
	trailingTray?: ReadonlyArray<MailSwipeSpec>;
	/** Share of the width a full swipe must cover. */
	commitRatio?: number;
	haptics?: boolean;
	/** Nudge the row once to show what a right swipe does (first run). */
	hint?: boolean;
	/** Settings preview: play a full swipe that way on a loop (a finger still takes over). */
	demo?: 'leading' | 'trailing' | null;
	disabled?: boolean;
	className?: string;
	children: React.ReactNode;
	'data-flx'?: string;
	/** Lets a list glide the row to a new place (Agents' useFlipMoves). */
	'data-flip'?: string;
}

export const MAIL_SWIPE_TONE: Record<MailSwipeTone, string> = {
	read: '#2f6fe4',
	archive: '#2e9d5b',
	delete: 'var(--status-danger, #e5484d)',
	flag: '#e8912d',
	reply: 'var(--brand-primary)',
	remind: '#c98f06',
	task: '#7b5cf0',
	move: '#5b6b82',
	pin: '#0f8b8d',
};

export function swipeSpecColor(spec: MailSwipeSpec): string {
	return spec.color ?? MAIL_SWIPE_TONE[spec.tone];
}

const SETTLE_MS = 300;
const FLY_MS = 200;
const COLLAPSE_MS = 180;
const HINT_PX = 48;
const HINT_DELAY_MS = 900;
const HINT_HOLD_MS = 1100;
const SETTLE_EASE = 'cubic-bezier(0.2, 0.9, 0.25, 1)';
const FLY_EASE = 'cubic-bezier(0.3, 0.6, 0.35, 1)';

interface Drag {
	pointerId: number;
	startX: number;
	startY: number;
	rest: number;
	axis: 'horizontal' | 'vertical' | null;
	width: number;
	offset: number;
	armed: boolean;
	reach: SwipeReach;
	lastX: number;
	lastT: number;
	velocity: number;
}

/** The row resting open on a tray, so opening another one (or tapping elsewhere) closes it. */
let closeOpenRow: (() => void) | null = null;

export function MailSwipeRow({
	leading,
	trailing,
	leadingLong = null,
	trailingLong = null,
	longRatio = DEFAULT_LONG_RATIO,
	variant = 'mail',
	leadingTray = [],
	trailingTray = [],
	commitRatio = DEFAULT_COMMIT_RATIO,
	haptics = true,
	hint = false,
	demo = null,
	disabled,
	className,
	children,
	...rest
}: MailSwipeRowProps) {
	const rootRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const dragRef = useRef<Drag | null>(null);
	const frameRef = useRef<number | null>(null);
	const timersRef = useRef<Array<number>>([]);
	const busyRef = useRef(false);
	const restRef = useRef(0);
	const swallowClickRef = useRef(false);
	const sides: SwipeSides = {
		leading: leading != null || leadingLong != null,
		trailing: trailing != null || trailingLong != null,
	};
	const reaches: SwipeReaches = {
		leading: {short: leading != null, long: leadingLong != null},
		trailing: {short: trailing != null, long: trailingLong != null},
	};
	const specFor = (direction: number, reach: SwipeReach): MailSwipeSpec | null =>
		direction > 0 ? (reach === 2 ? leadingLong : leading) : reach === 2 ? trailingLong : trailing;
	const trays: SwipeTrays = {leading: trayWidth(leadingTray.length), trailing: trayWidth(trailingTray.length)};

	const later = (fn: () => void, ms: number) => {
		timersRef.current.push(window.setTimeout(fn, ms));
	};
	const duration = (ms: number) => (Accessibility.useReducedMotion ? Math.min(ms, 80) : ms);
	const tick = () => {
		if (haptics) hapticTap();
	};

	const paint = (offset: number, armed: boolean | SwipeReach, width: number) => {
		const root = rootRef.current;
		const content = contentRef.current;
		if (!root || !content) return;
		if (offset === 0) delete root.dataset.side;
		else root.dataset.side = offset > 0 ? 'leading' : 'trailing';
		if (armed) root.dataset.armed = '';
		else delete root.dataset.armed;
		if (armed === 2) root.dataset.reach = 'long';
		else delete root.dataset.reach;
		root.style.setProperty('--swipe-icon-x', `${iconCentre(offset)}px`);
		root.style.setProperty('--swipe-progress', swipeProgress(offset, width, commitRatio).toFixed(3));
		content.style.transform = offset === 0 ? '' : `translate3d(${offset}px, 0, 0)`;
	};

	const clearOpen = () => {
		restRef.current = 0;
		const root = rootRef.current;
		if (root) delete root.dataset.open;
		if (closeOpenRow === closeTray) closeOpenRow = null;
	};

	const reset = () => {
		const root = rootRef.current;
		const content = contentRef.current;
		busyRef.current = false;
		if (!root || !content) return;
		for (const key of ['side', 'armed', 'reach', 'dragging', 'flying', 'open', 'flyTone']) delete root.dataset[key];
		root.style.removeProperty('height');
		root.style.removeProperty('transition');
		root.style.removeProperty('--swipe-fly-tone');
		content.style.transition = '';
		content.style.transform = '';
	};

	/** Animate the row to `target` (0 = rest, else a tray) and settle there. */
	const settle = (target = 0) => {
		const root = rootRef.current;
		const content = contentRef.current;
		if (!root || !content) return;
		busyRef.current = true;
		delete root.dataset.dragging;
		const ms = duration(SETTLE_MS);
		content.style.transition = `transform ${ms}ms ${SETTLE_EASE}`;
		content.style.transform = `translate3d(${target}px, 0, 0)`;
		if (target === 0) {
			clearOpen();
			later(reset, ms + 20);
			return;
		}
		restRef.current = target;
		root.dataset.open = '';
		if (closeOpenRow && closeOpenRow !== closeTray) closeOpenRow();
		closeOpenRow = closeTray;
		later(() => {
			busyRef.current = false;
			content.style.transition = '';
		}, ms + 20);
	};

	function closeTray() {
		if (restRef.current === 0) return;
		settle(0);
	}

	const flyOut = (direction: number, spec: MailSwipeSpec, fromTray: boolean, reach: SwipeReach = 1) => {
		const root = rootRef.current;
		const content = contentRef.current;
		if (!root || !content) return;
		clearOpen();
		busyRef.current = true;
		delete root.dataset.dragging;
		root.dataset.flying = '';
		root.dataset.armed = '';
		root.dataset.side = direction > 0 ? 'leading' : 'trailing';
		if (reach === 2) root.dataset.reach = 'long';
		if (fromTray) {
			// The track takes the tray action's colour; the full action's icon would be wrong here.
			root.dataset.flyTone = spec.tone;
			root.style.setProperty('--swipe-fly-tone', swipeSpecColor(spec));
		}
		const width = root.offsetWidth;
		const fly = duration(FLY_MS);
		content.style.transition = `transform ${fly}ms ${FLY_EASE}`;
		content.style.transform = `translate3d(${direction * (width + 8)}px, 0, 0)`;
		root.style.setProperty('--swipe-icon-x', `${iconCentre(width)}px`);
		later(() => {
			root.style.height = `${root.offsetHeight}px`;
			void root.offsetHeight;
			const collapse = duration(COLLAPSE_MS);
			root.style.transition = `height ${collapse}ms ease`;
			root.style.height = '0px';
			later(() => {
				spec.run();
				// Normally the row is gone by now; if the action kept it, show it again.
				later(reset, 400);
			}, collapse);
		}, fly);
	};

	const runTrayAction = (spec: MailSwipeSpec, side: 1 | -1) => {
		if (spec.removes) {
			flyOut(side, spec, true);
			return;
		}
		settle(0);
		spec.run();
	};

	useEffect(
		() => () => {
			for (const timer of timersRef.current) window.clearTimeout(timer);
			if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
			if (closeOpenRow === closeTray) closeOpenRow = null;
		},
		[],
	);

	// While a tray is open, a press anywhere outside this row closes it.
	useEffect(() => {
		const onDown = (event: PointerEvent) => {
			if (restRef.current === 0) return;
			if (rootRef.current?.contains(event.target as Node)) return;
			closeTray();
		};
		document.addEventListener('pointerdown', onDown, true);
		return () => document.removeEventListener('pointerdown', onDown, true);
	}, []);

	// A sideways drag owns the touch: stop the list from scrolling under it,
	// which would otherwise cancel the pointer mid-swipe.
	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;
		const onTouchMove = (event: TouchEvent) => {
			if (dragRef.current?.axis === 'horizontal' && event.cancelable) event.preventDefault();
		};
		root.addEventListener('touchmove', onTouchMove, {passive: false});
		return () => root.removeEventListener('touchmove', onTouchMove);
	}, []);

	useEffect(() => {
		if (disabled && restRef.current !== 0) settle(0);
	}, [disabled]);

	// First run: slide the row a little to show the right-swipe action, then back.
	useEffect(() => {
		if (!hint || !leading || disabled) return;
		const start = window.setTimeout(() => {
			const root = rootRef.current;
			const content = contentRef.current;
			if (!root || !content || busyRef.current || dragRef.current) return;
			busyRef.current = true;
			content.style.transition = `transform ${duration(420)}ms ${SETTLE_EASE}`;
			paint(HINT_PX, false, root.offsetWidth);
			later(() => settle(0), duration(420) + HINT_HOLD_MS);
		}, HINT_DELAY_MS);
		timersRef.current.push(start);
	}, [hint]);

	// Settings preview: pull past the line, hold armed, spring back, repeat.
	useEffect(() => {
		const spec = demo === 'leading' ? leading : demo === 'trailing' ? trailing : null;
		if (!spec) return;
		let frame = 0;
		let stopped = false;
		const play = () => {
			const root = rootRef.current;
			if (!root || dragRef.current || restRef.current !== 0) return;
			const width = root.offsetWidth;
			const sign = demo === 'leading' ? 1 : -1;
			const target = commitDistance(width, commitRatio) + 36;
			const started = performance.now();
			let armed = false;
			busyRef.current = true;
			const step = (now: number) => {
				if (stopped || dragRef.current) return;
				const t = Math.min(1, (now - started) / 900);
				const offset = sign * target * (1 - (1 - t) ** 3);
				armed = isArmed(offset, width, sides, armed, commitRatio);
				paint(offset, armed, width);
				if (t < 1) frame = requestAnimationFrame(step);
				else later(() => settle(0), 650);
			};
			frame = requestAnimationFrame(step);
		};
		const first = window.setTimeout(play, 400);
		const loop = window.setInterval(play, 3200);
		return () => {
			stopped = true;
			cancelAnimationFrame(frame);
			window.clearTimeout(first);
			window.clearInterval(loop);
			reset();
		};
	}, [demo, commitRatio, leading?.key, trailing?.key]);

	const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (disabled || busyRef.current || event.pointerType === 'mouse') return;
		if (swipeBackOwnsTouch(event.clientX)) return;
		if ((event.target as HTMLElement).closest('[data-swipe-tray]')) return;
		if (!sides.leading && !sides.trailing && trays.leading === 0 && trays.trailing === 0) return;
		if (closeOpenRow && closeOpenRow !== closeTray) closeOpenRow();
		dragRef.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			rest: restRef.current,
			axis: null,
			width: rootRef.current?.offsetWidth ?? window.innerWidth,
			offset: restRef.current,
			armed: false,
			reach: 0,
			lastX: event.clientX,
			lastT: event.timeStamp,
			velocity: 0,
		};
	};

	const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		const drag = dragRef.current;
		if (!drag || drag.pointerId !== event.pointerId) return;
		const dx = event.clientX - drag.startX;
		if (drag.axis == null) {
			drag.axis = resolveSwipeAxis(dx, event.clientY - drag.startY);
			if (drag.axis == null) return;
			if (drag.axis === 'vertical') {
				dragRef.current = null;
				return;
			}
			const root = rootRef.current;
			if (root) {
				root.dataset.dragging = '';
				delete root.dataset.open;
				// The row inside (LongPressable) lets go of the pointer once it moves; take it,
				// or a finger that drifts off the row lifts somewhere else and the swipe hangs.
				try {
					root.setPointerCapture(event.pointerId);
				} catch {}
			}
			if (contentRef.current) contentRef.current.style.transition = '';
		}
		const dt = event.timeStamp - drag.lastT;
		if (dt > 0) drag.velocity = 0.7 * ((event.clientX - drag.lastX) / dt) + 0.3 * drag.velocity;
		drag.lastX = event.clientX;
		drag.lastT = event.timeStamp;
		drag.offset = dragOffset(drag.rest + dx, drag.width, sides, trays);
		const reach = swipeReach(drag.offset, drag.width, reaches, drag.reach, commitRatio, longRatio);
		// One tick per line crossed on the way out, again after pulling back and re-arming.
		if (reach > drag.reach) tick();
		drag.reach = reach;
		drag.armed = reach > 0;
		if (frameRef.current == null) {
			frameRef.current = requestAnimationFrame(() => {
				frameRef.current = null;
				const current = dragRef.current;
				if (current) paint(current.offset, current.reach, current.width);
			});
		}
	};

	const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		const drag = dragRef.current;
		if (!drag || drag.pointerId !== event.pointerId) return;
		dragRef.current = null;
		if (drag.axis !== 'horizontal') return;
		if (frameRef.current != null) {
			cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
		}
		// The click that follows a drag is not a tap; forget it if none comes.
		swallowClickRef.current = true;
		later(() => {
			swallowClickRef.current = false;
		}, 400);
		// A stale sample from a finger that paused before lifting is not a flick.
		const velocity = event.timeStamp - drag.lastT > 80 ? 0 : drag.velocity;
		// The system taking the touch still counts as letting go once the colour has flooded.
		const outcome =
			event.type === 'pointercancel' && !drag.armed
				? 'cancel'
				: releaseOutcome(drag.offset, velocity, sides, drag.armed, trays);
		const direction = drag.offset > 0 ? 1 : -1;
		// A flick short of the first line runs the first action (never the second).
		const reach: SwipeReach = drag.reach === 2 ? 2 : 1;
		const spec = specFor(direction, reach);
		if (outcome === 'commit' && spec) {
			if (!drag.armed) tick();
			paint(drag.offset, reach, drag.width);
			clearOpen();
			if (spec.removes) {
				flyOut(direction, spec, false, reach);
				return;
			}
			spec.run();
			settle(0);
			return;
		}
		paint(drag.offset, drag.reach, drag.width);
		if (outcome === 'tray') {
			settle(direction * (direction > 0 ? trays.leading : trays.trailing));
			return;
		}
		settle(0);
	};

	const onClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
		if ((event.target as HTMLElement).closest('[data-swipe-tray]')) return;
		if (swallowClickRef.current || restRef.current !== 0) {
			swallowClickRef.current = false;
			event.preventDefault();
			event.stopPropagation();
			// A tap on a row resting open closes its tray instead of opening the mail.
			if (restRef.current !== 0) closeTray();
		}
	};

	const renderTray = (actions: ReadonlyArray<MailSwipeSpec>, side: 'leading' | 'trailing') => {
		if (actions.length === 0) return null;
		return (
			<div
				className={clsx(styles.tray, side === 'leading' ? styles.trayLeading : styles.trayTrailing)}
				style={{width: trayWidth(actions.length)}}
				data-swipe-tray=""
				data-flx={`konvo-mail.swipe-row.tray-${side}`}
			>
				{actions.map((action) => (
					<button
						key={action.key}
						type="button"
						className={styles.trayButton}
						style={{'--tray-tone': swipeSpecColor(action)} as React.CSSProperties}
						onClick={(event) => {
							event.stopPropagation();
							runTrayAction(action, side === 'leading' ? 1 : -1);
						}}
						data-flx={`konvo-mail.swipe-row.tray-action-${action.key}`}
					>
						{action.icon}
						<span>{action.label}</span>
					</button>
				))}
			</div>
		);
	};

	const card = variant === 'card';
	const renderGlyph = (spec: MailSwipeSpec | null, long: boolean) =>
		spec ? (
			<span className={clsx(styles.glyph, long ? styles.glyphLong : styles.glyphShort)}>
				{spec.icon}
				{card && <span className={styles.slotLabel}>{spec.label}</span>}
			</span>
		) : null;

	return (
		<div
			ref={rootRef}
			className={clsx(styles.swipe, className)}
			style={
				{
					'--swipe-leading-tone': leading ? swipeSpecColor(leading) : undefined,
					'--swipe-trailing-tone': trailing ? swipeSpecColor(trailing) : undefined,
					'--swipe-leading-long-tone': leadingLong ? swipeSpecColor(leadingLong) : undefined,
					'--swipe-trailing-long-tone': trailingLong ? swipeSpecColor(trailingLong) : undefined,
				} as React.CSSProperties
			}
			data-variant={card ? 'card' : undefined}
			data-leading-tray={leadingTray.length > 0 || undefined}
			data-trailing-tray={trailingTray.length > 0 || undefined}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={endDrag}
			onPointerCancel={endDrag}
			onLostPointerCapture={(event) => {
				// Only the row's own capture; the row inside releasing its capture bubbles here too.
				if (event.target === rootRef.current) endDrag(event);
			}}
			onClickCapture={onClickCapture}
			{...rest}
		>
			<div className={styles.track} aria-hidden="true">
				{leading || leadingLong ? (
					<span className={clsx(styles.slot, styles.slotLeading)}>
						{renderGlyph(leading, false)}
						{renderGlyph(leadingLong, true)}
					</span>
				) : null}
				{trailing || trailingLong ? (
					<span className={clsx(styles.slot, styles.slotTrailing)}>
						{renderGlyph(trailing, false)}
						{renderGlyph(trailingLong, true)}
					</span>
				) : null}
			</div>
			{renderTray(leadingTray, 'leading')}
			{renderTray(trailingTray, 'trailing')}
			<div ref={contentRef} className={styles.content}>
				{children}
			</div>
		</div>
	);
}
